// T31 — compact play/scrub/speed row for temporal replay on map panels. Pure presentational; every
// prop comes from `useTemporalReplay` (via the panel's `useSpatialResultsMapReplay` glue), and the
// panel mounts this component ONLY when `timeColumn` is non-null (progressive disclosure). Placed
// top-anchored so it never collides with the bottom-anchored legend / ghost / probe overlays.

import { Play, Pause } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import type { TemporalReplaySpeed } from './useTemporalReplay';

const SPEEDS: TemporalReplaySpeed[] = [1, 2, 4];

function formatCursor(t: number): string {
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export function TemporalReplayControl({
  range,
  cursor,
  playing,
  speed,
  onPlay,
  onPause,
  onSeek,
  onSpeedChange,
}: {
  range: [number, number];
  cursor: number;
  playing: boolean;
  speed: TemporalReplaySpeed;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (t: number) => void;
  onSpeedChange: (speed: TemporalReplaySpeed) => void;
}) {
  return (
    <div
      data-testid="spatial-replay-control"
      className="pointer-events-auto absolute inset-x-2 top-2 z-10 flex items-center gap-2 rounded-md border border-border/60 bg-background/90 px-2 py-1.5 text-xs shadow-sm backdrop-blur-sm"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 flex-shrink-0"
        aria-label={playing ? 'Pause replay' : 'Play replay'}
        onClick={playing ? onPause : onPlay}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </Button>
      <input
        type="range"
        data-testid="spatial-replay-scrubber"
        aria-label="Replay position"
        min={range[0]}
        max={range[1]}
        step={Math.max(1, (range[1] - range[0]) / 1000)}
        value={cursor}
        onChange={(e) => onSeek(Number(e.target.value))}
        className="h-1.5 flex-1 accent-primary"
      />
      <Select value={String(speed)} onValueChange={(v) => onSpeedChange(Number(v) as TemporalReplaySpeed)}>
        <SelectTrigger className="h-6 w-14 flex-shrink-0 text-xs" data-testid="spatial-replay-speed">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SPEEDS.map((s) => (
            <SelectItem key={s} value={String(s)} className="text-xs">
              {s}×
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span
        className="hidden flex-shrink-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground sm:inline"
        data-testid="spatial-replay-timestamp"
      >
        {formatCursor(cursor)}
      </span>
    </div>
  );
}
